import React from 'react';

// ============================================================
// Rich text markers — plain-text safe, sabai editor ra public
// article view ma kaam garchha:
//   **bold**          → बोल्ड
//   *italic*          → इटालिक
//   >> line           → center align paragraph
//   • ○ ▪ ►  prefix  → 4 bullet list styles
// ============================================================

export const BULLET_CHARS = ['•', '○', '▪', '►'];

const BULLET_PREFIX_RE = /^([•○▪►])\s+/;
const CENTER_PREFIX_RE = /^>>(?:\s+)?/;
const ALL_LIST_RE = /^([•○▪►]\s+|>>\s?)/;

// ---------- editor helpers (NewsForm toolbar) ----------

function lineBounds(text, pos) {
  const start = text.lastIndexOf('\n', Math.max(pos - 1, 0)) + 1;
  let end = text.indexOf('\n', pos);
  if (end === -1) end = text.length;
  return [start, end];
}

function selectedBlockBounds(text, selStart, selEnd) {
  const s = Math.min(selStart, selEnd);
  const e = Math.max(selStart, selEnd);
  const [blockStart] = lineBounds(text, s);
  const [, blockEnd] = lineBounds(text, e);
  return [blockStart, blockEnd];
}

// **bold** / *italic* — wrap or unwrap current selection
export function applyInlineFormat(text, selStart, selEnd, marker) {
  const before = text.slice(0, selStart);
  const sel = text.slice(selStart, selEnd);
  const after = text.slice(selEnd);
  const m = marker.length;

  // Selection already wrapped by markers outside → unwrap
  if (sel && before.endsWith(marker) && after.startsWith(marker)) {
    return {
      text: before.slice(0, -m) + sel + after.slice(m),
      selectionStart: selStart - m,
      selectionEnd: selEnd - m,
    };
  }
  // Selection itself contains markers → unwrap inside
  if (sel.startsWith(marker) && sel.endsWith(marker) && sel.length >= m * 2) {
    const inner = sel.slice(m, -m);
    return {
      text: before + inner + after,
      selectionStart: selStart,
      selectionEnd: selStart + inner.length,
    };
  }

  const body = sel || 'यहाँ लेख्नुहोस्';
  return {
    text: `${before}${marker}${body}${marker}${after}`,
    selectionStart: selStart + m,
    selectionEnd: selStart + m + body.length,
  };
}

// Line-level prefix (bullets + >> center) — toggle & switch designs
export function toggleLinePrefix(text, selStart, selEnd, prefix) {
  const [blockStart, blockEnd] = selectedBlockBounds(text, selStart, selEnd);
  const block = text.slice(blockStart, blockEnd);
  const lines = block.split('\n');

  const meaningful = lines.filter((l) => l.trim() !== '');
  const allHave = meaningful.length > 0 && meaningful.every((l) => l.startsWith(prefix));

  const nextLines = lines.map((l) => {
    if (l.trim() === '') return l;
    const stripped = l.replace(ALL_LIST_RE, '');
    if (allHave) return stripped; // toggle off
    return prefix + stripped; // add / switch design
  });

  const newBlock = nextLines.join('\n');
  return {
    text: text.slice(0, blockStart) + newBlock + text.slice(blockEnd),
    selectionStart: blockStart,
    selectionEnd: blockStart + newBlock.length,
  };
}

// ---------- renderer (ArticleView etc.) ----------

function parseInlineSpans(text) {
  const spans = [];
  const re = /\*\*([^*\n]+)\*\*|\*([^*\n]+)\*/g;
  let last = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      spans.push({ text: text.slice(last, match.index), bold: false, italic: false });
    }
    if (match[1] !== undefined) {
      spans.push({ text: match[1], bold: true, italic: false });
    } else {
      spans.push({ text: match[2], bold: false, italic: true });
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    spans.push({ text: text.slice(last), bold: false, italic: false });
  }
  return spans;
}

export function parseRichContent(text) {
  return String(text || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((raw) => {
      let body = raw;
      let bullet = null;
      let align = 'left';

      const bm = body.match(BULLET_PREFIX_RE);
      if (bm) {
        bullet = bm[1];
        body = body.slice(bm[0].length);
      }
      if (CENTER_PREFIX_RE.test(body)) {
        align = 'center';
        body = body.replace(CENTER_PREFIX_RE, '');
      }
      return { raw, text: body, bullet, align, spans: parseInlineSpans(body) };
    });
}

// TTS / excerpt ko lagi marker-free plain text
export function stripRichMarkers(text) {
  return parseRichContent(text)
    .map((p) => (p.bullet ? `${p.bullet} ${p.text}` : p.text))
    .join('\n');
}

export function renderInlineSpans(spans) {
  return spans.map((s, i) => {
    if (s.bold) return <strong key={`b${i}`} className="font-bold">{s.text}</strong>;
    if (s.italic) return <em key={`i${i}`}>{s.text}</em>;
    return <React.Fragment key={`t${i}`}>{s.text}</React.Fragment>;
  });
}
